import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { ConfirmDialog } from '../../../common/views/modals/confirmDialog/ConfirmDialog';
import { useDeleteAddedAssetMutation } from '../../../../redux/api/addedAssetsApi';
import { addedAssetsMessages as messages } from './addedAssetsMessages';
import { removeAddedErrorKind } from './addedErrors';

interface RemoveAddedAssetDialogProps {
  accountId: number;
  catalogItemUuid: string;
  /** The added version to remove; null closes the dialog. */
  assetUuid: string | null;
  /** The name of the asset type ("music video"). */
  name: string;
  onClose: () => void;
}

/**
 * "Remove" of an added version (spec 2026-10-09 §3.1): the server deletes the row, its file and its
 * YouTube record; the version before it is current again. The video on YouTube or on the computer
 * of the artist stays. A video with a live publish is refused (`M24_ADDED_PUBLISHED`) with its own
 * sentence.
 */
const RemoveAddedAssetDialogComponent: React.FC<RemoveAddedAssetDialogProps> = (props) => {
  const { accountId, catalogItemUuid, assetUuid, name, onClose } = props;
  const intl = useIntl();
  const [remove, state] = useDeleteAddedAssetMutation();
  const confirm = useCallback(() => {
    if (assetUuid === null) {
      return;
    }
    remove({ accountId, catalogItemUuid, assetUuid }).unwrap().then(onClose).catch(console.error);
  }, [accountId, assetUuid, catalogItemUuid, onClose, remove]);

  return (
    <ConfirmDialog
      isOpen={assetUuid !== null}
      tone="danger"
      title={intl.formatMessage(messages.removeTitle, { name: name.toLowerCase() })}
      description={intl.formatMessage(messages.removeBody)}
      confirmLabel={intl.formatMessage(messages.removeConfirm)}
      isBusy={state.isLoading}
      error={
        state.isError
          ? intl.formatMessage(
              removeAddedErrorKind(state.error) === 'published' ? messages.removePublished : messages.removeFailed
            )
          : undefined
      }
      onConfirm={confirm}
      onCancel={onClose}
      testId="remove-added-asset"
    />
  );
};

export const RemoveAddedAssetDialog = TypedMemo(RemoveAddedAssetDialogComponent);
